/**
 * Lucky Gift 动态数据 Hook
 * 用于 Plaza 页面的轻量级数据获取
 */

import { useCallback, useEffect, useState } from "react";
import { LuckyGiftClient, type PublicGiftSummary, type MyClaimRecordEnriched } from "@/lib/clients/LuckyGiftClient";
import { createLogAdapter } from "@/lib/logger/console-adapter";

const uiLog = createLogAdapter("app/plaza/hooks/useLuckyGiftDynamics.ts");

export interface GiftDynamicMessage {
	id: string;
	type: "claim";
	timestamp: bigint;
	claimer: string;
	sender: string;
	amount: bigint;
	tokenInfo: {
		tokenId: string;
		displayUnitName: string;
		displayToChainDecimals: number;
	};
	message: string;
}

export function useLuckyGiftDynamics() {
	const [activeGifts, setActiveGifts] = useState<PublicGiftSummary[]>([]);
	const [dynamics, setDynamics] = useState<GiftDynamicMessage[]>([]);
	const [isLoading, setIsLoading] = useState(true);
	const [error, setError] = useState<Error | null>(null);
	const [totalGifts, setTotalGifts] = useState(0);

	const fetchDynamics = useCallback(async (silent = false) => {
		try {
			if (!silent) setIsLoading(true);
			setError(null);

			const client = new LuckyGiftClient(null);

			// 并行获取活跃红包和领取动态
			const [activeGiftsRes, claimRecordsRes] = await Promise.all([
				client.getPublicActiveGifts(null, { field: { CreateTime: null }, order: { Desc: null } }, 30n, 0n),
				client.queryAllClaimRecords(undefined, { Forward: null }, 20n)
			]);

			// 1. 处理活跃红包 (取前3个)
			setActiveGifts(activeGiftsRes.gifts.slice(0, 3));
			setTotalGifts(Number(activeGiftsRes.total));

			// 2. 转换领取动态
			const messages: GiftDynamicMessage[] = claimRecordsRes.data.map(record => ({
				id: `${record.giftId}-${record.id}`,
				type: "claim",
				timestamp: record.claimTime,
				claimer: record.claimer.toText(),
				sender: record.sender.toText(),
				amount: record.amount,
				tokenInfo: {
					tokenId: record.tokenInfo.tokenId,
					displayUnitName: record.tokenInfo.displayUnitName || record.tokenInfo.tokenName,
					displayToChainDecimals: record.tokenInfo.displayToChainDecimals,
				},
				message: record.giftMemo?.[0] || ""
			}));

			setDynamics(messages);
		} catch (err) {
			uiLog.error("Failed to fetch lucky gift dynamics:", err);
			setError(err as Error);
		} finally {
			if (!silent) setIsLoading(false);
		}
	}, []);

	// 初始加载
	useEffect(() => {
		fetchDynamics();
	}, [fetchDynamics]);

	// 定时刷新 (每30秒)
	useEffect(() => {
		const timer = setInterval(() => {
			fetchDynamics(true);
		}, 30000);
		return () => clearInterval(timer);
	}, [fetchDynamics]);

	return {
		activeGifts,
		dynamics,
		totalGifts,
		isLoading,
		error,
		refresh: fetchDynamics,
	};
}
